import { Button, Card, CardContent, Chip, Container, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import MessagePage from "../../components/MessagePage";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { Assessment } from "../../globals/types";
import { useAssessments } from "../../hooks/useFormsLists";
import useToken from "../../hooks/useToken";
import { isNotFound } from "../../lib/formsApi";

import { getAssessmentStatus } from "./getAssessmentStatus";

function getRfiProgress(assessments: Assessment[]) {
    let unsubmittedCount = 0;
    let overallProgress: "InProgress" | "Submitted" | "NotStarted" = "NotStarted";
    for (const assessment of assessments) {
        const status = getAssessmentStatus(assessment);
        if (status === "Submitted") {
            overallProgress = "InProgress";
        } else if (status === "InProgress") {
            overallProgress = "InProgress";
            unsubmittedCount++;
        } else if (status === "NotStarted") {
            unsubmittedCount++;
        }
    }

    if (unsubmittedCount === 0) {
        overallProgress = "Submitted";
    }

    return { overallProgress, unsubmittedCount };
}

export const StartNowPage: React.FC = () => {
    const { data, error } = useAssessments();
    const { formatMessage } = useIntl();
    const { token, decoded } = useToken();
    const customerName = decoded.customerName;
    const notFound = isNotFound(error);

    useEffect(() => {
        if (error && !notFound) {
            Sentry.captureException(error, {
                extra: {
                    assessmentIds: decoded.assessmentIds,
                    tenant: decoded.tenant,
                    campaignId: decoded.campaignId,
                    customerName: decoded.customerName,
                },
            });
        }
    }, [error, notFound, decoded]);

    if (notFound || data?.length === 0) {
        return <MessagePage reason="404" />;
    }

    if (error) {
        return <MessagePage reason="other" />;
    }

    let supplierName = data?.[0]?.supplierName;
    if (!supplierName) {
        supplierName = formatMessage({ defaultMessage: "No supplier" });
    }

    const { overallProgress, unsubmittedCount } = getRfiProgress(data ?? []);

    return (
        <Container sx={{ height: "100vh" }} maxWidth="md" disableGutters={false}>
            <Stack alignItems="center" justifyContent="flex-start" height="100%" gap={4}>
                <Stack height="75%" justifyContent="center" alignItems="center" py={4} minHeight="min-content">
                    <Card>
                        <CardContent>
                            <Stack alignItems="center" spacing={4.5}>
                                <Typography fontWeight="bold" variant="textXl">
                                    {supplierName}
                                </Typography>
                                <Typography fontWeight="bold" variant="textXl">
                                    <FormattedMessage
                                        defaultMessage="{overallProgress, select,
                                            InProgress {{count, plural,
                                                one {You have <chip>{count}</chip> remaining request}
                                                other {You have <chip>{count}</chip> remaining requests}
                                            }}
                                            NotStarted {{count, plural,
                                                one {You have <chip>{count}</chip> new request}
                                                other {You have <chip>{count}</chip> new requests}
                                            }}
                                            other {You have submitted all requests}
                                        }"
                                        description="Invitation page title"
                                        values={{
                                            overallProgress,
                                            count: unsubmittedCount,
                                            chip: (chunks) => <Chip label={chunks} size="small" />,
                                        }}
                                    />
                                </Typography>
                                <Typography>
                                    <FormattedMessage
                                        defaultMessage="<bold>{customerName}</bold> has sent you a request to submit information"
                                        description="Invitation page supplier"
                                        values={{
                                            customerName,
                                            bold: (chunks) => (
                                                <Typography component="span" fontWeight={700}>
                                                    {chunks}
                                                </Typography>
                                            ),
                                        }}
                                    />
                                </Typography>
                                <Button
                                    size="small"
                                    color="primary"
                                    component={Link}
                                    to={{
                                        pathname: "overview",
                                        search: `?token=${token}`,
                                    }}
                                >
                                    <FormattedMessage
                                        defaultMessage="{progress, select, InProgress {Continue} Submitted {View answers} other {Start now}}"
                                        values={{ progress: overallProgress }}
                                    />
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
                <Stack mb={12}>
                    <PoweredByFooter />
                </Stack>
            </Stack>
        </Container>
    );
};
